import type { Carer } from '@lmc/ts-common'
import { getFullName } from '@lmc/ts-common/lib/utils/functions'
import { Button, Form, FormItem, Input } from '@lmc/ui-kit'
import { Heading, Text } from '@lmc/ui-kit/src/general'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import { Form as AntdForm, Modal } from 'antd'
import { showToast } from 'common/utils'
import useUser from 'hooks/useUser/useUser'
import { NAMESPACE } from 'locale'
import { generalConstants } from 'locale/general'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { commonPatterns } from '../../../../utils/enhancedTestIds'

export type DeleteMemberProps = {
  open: boolean
  onClose: () => void
  carer?: Carer
  testId?: string
}

const DeleteProfileModal = ({
  open,
  onClose,
  carer,
  testId
}: DeleteMemberProps) => {
  const { t } = useTranslation(NAMESPACE.GENERAL)

  const [form] = AntdForm.useForm()

  const fullName = useMemo(
    () => (carer ? getFullName(carer.name.first, carer.name.last) : undefined),
    [carer]
  )

  const { deleteUser, isLoadingDelete } = useUser({
    onDeleteSuccess: () => onClose(),
    onErrorCustom: (err) => {
      const message = (err as { message: string }).message as string
      showToast({
        title: message,
        type: 'error'
      })
      form.setFields([
        {
          name: 'fullName',
          errors: [message]
        }
      ])
    },
    id: carer?.id
  })

  const onSubmit = async () => {
    deleteUser()
  }

  const validateMatch = (rule: any, value: string) => {
    const expectedValue = fullName
    if (value === expectedValue) {
      return Promise.resolve()
    }
    return Promise.reject(new Error(t(generalConstants.TYPE_NAME)))
  }

  return (
    <Modal
      destroyOnClose
      width={580}
      title={
        <ModalHeader data-testid={testId ? `${testId}-header` : undefined}>
          <Title level={1} data-testid={testId ? `${testId}-title` : undefined}>
            {t(generalConstants.DELETE_PROFILE)}
          </Title>
        </ModalHeader>
      }
      footer={
        <ModalFooter data-testid={testId ? `${testId}-footer` : undefined}>
          <Button
            onClick={onClose}
            data-testid={
              testId ? commonPatterns.button(testId, 'cancel') : undefined
            }
          >
            {t(generalConstants.CANCEL)}
          </Button>
          <Button
            onClick={form.submit}
            isLoading={isLoadingDelete}
            variant="primary"
            data-testid={
              testId ? commonPatterns.button(testId, 'confirm') : undefined
            }
          >
            {t(generalConstants.CONFIRM, {})}
          </Button>
        </ModalFooter>
      }
      open={open}
      onCancel={onClose}
      data-testid={testId}
    >
      <Text data-testid={testId ? `${testId}-description` : undefined}>
        Are you sure you want to delete <strong>{fullName}</strong> ? If you go
        ahead, it can't be undone, Once the record is gone, it's gone for good!
      </Text>
      <WrapperMiddle data-testid={testId ? `${testId}-warning` : undefined}>
        <Text>{t(generalConstants.MAKE_SURE_TO_DELETE)}</Text>
      </WrapperMiddle>
      <WrapperType data-testid={testId ? `${testId}-instruction` : undefined}>
        <Text>
          <strong>{t(generalConstants.TYPE_NAME)}</strong>
        </Text>
      </WrapperType>
      <StyledForm
        form={form}
        layout="vertical"
        onFinish={onSubmit}
        data-testid={testId ? `${testId}-form` : undefined}
      >
        <div
          data-testid={
            testId ? commonPatterns.field(testId, 'name') : undefined
          }
        >
          <FormItem
            emptyLabel
            name="fullName"
            label={t(generalConstants.EMAIL)}
            style={{ width: '100%' }}
            rules={[
              { required: true, message: 'This field is required' },
              { validator: validateMatch }
            ]}
          >
            <Input
              placeholder={fullName}
              data-testid={
                testId ? commonPatterns.input(testId, 'name') : undefined
              }
            />
          </FormItem>
        </div>
      </StyledForm>
      <ModalDivider />
    </Modal>
  )
}

export default DeleteProfileModal

const Title = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3};
  text-transform: none;
  font-weight: 500;
  font-size: 24px;
  line-height: 32px;
`

const ModalDivider = styled(Divider)`
  margin: 24px 0px;
`

const WrapperType = styled.div`
  margin-bottom: -20px;
  margin-top: 20px;
`

const WrapperMiddle = styled.div`
  margin-top: 20px;
`

const StyledForm = styled(Form)`
  gap: 24px;
`

const ModalFooter = styled.div`
  display: flex;
  gap: 16px;
`

const ModalHeader = styled.div`
  display: flex;
  flex-direction: column;
  text-transform: none;
`
